import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  ArrowLeft, MapPin, Crown, Search, ArrowUpRight, Building2, Sparkles
} from "lucide-react";
import { MASTER_PARTNERS, REGION_GROUPS, getPartnersByRegion } from "@/constants/masterPartners";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const MASTER_PARTNER_PHOTO = "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=85";

const MPCard = ({ mp, onClick }) => (
  <motion.button
    whileHover={{ y: -6 }}
    onClick={onClick}
    data-testid={`hm-mp-${mp.slug}`}
    className="group text-left relative rounded-3xl overflow-hidden bg-slate-900/60 backdrop-blur-xl border border-white/10 hover:border-[#CCD500]/40 transition-colors w-full"
  >
    <div className="relative h-44 overflow-hidden">
      <img src={mp.img} alt={mp.city} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
      <div className="absolute top-3 left-3 flex gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full bg-[#CCD500] text-black">{mp.tag}</span>
        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-slate-950/70 backdrop-blur-sm text-white border border-white/10">{mp.code}</span>
      </div>
      <div className="absolute bottom-3 left-4 right-4">
        <p className="text-xs text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
          <MapPin className="w-3 h-3" /> {mp.city}
        </p>
        <h4 className="font-display text-xl font-bold text-white leading-tight">{mp.mp}</h4>
      </div>
    </div>
    <div className="p-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <img src={MASTER_PARTNER_PHOTO} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-[#CCD500]/40 shrink-0" />
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Propiedades</p>
          <p className="font-display text-base font-black text-[#CCD500]">{mp.props} activas</p>
        </div>
      </div>
      <span className="text-xs font-semibold text-[#CCD500] flex items-center gap-1 group-hover:gap-2 transition-all">
        Ver <ArrowUpRight className="w-3.5 h-3.5" />
      </span>
    </div>
  </motion.button>
);

const REGION_META = {
  "Noroeste":  { color: "from-cyan-500/20 to-transparent" },
  "Norte":     { color: "from-slate-500/20 to-transparent" },
  "Noreste":   { color: "from-fuchsia-500/20 to-transparent" },
  "Occidente": { color: "from-amber-500/20 to-transparent" },
  "Bajío":     { color: "from-violet-500/20 to-transparent" },
  "Centro":    { color: "from-rose-500/20 to-transparent" },
  "Oriente":   { color: "from-blue-500/20 to-transparent" },
  "Sureste":   { color: "from-teal-500/20 to-transparent" },
  "Suroeste":  { color: "from-emerald-500/20 to-transparent" },
};

export const HazMatchPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeRegion, setActiveRegion] = useState("Todas");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return MASTER_PARTNERS.filter((mp) => {
      if (activeRegion !== "Todas" && mp.regionGroup !== activeRegion) return false;
      if (!q) return true;
      return (
        mp.city.toLowerCase().includes(q) ||
        mp.mp.toLowerCase().includes(q) ||
        mp.state.toLowerCase().includes(q) ||
        mp.coverage.some((c) => c.toLowerCase().includes(q))
      );
    });
  }, [query, activeRegion]);

  const grouped = useMemo(() => {
    const obj = {};
    for (const mp of filtered) {
      if (!obj[mp.regionGroup]) obj[mp.regionGroup] = [];
      obj[mp.regionGroup].push(mp);
    }
    return obj;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-body selection:bg-[#CCD500] selection:text-black">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B1120]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="hm-back-btn" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Volver al inicio</span>
          </button>
          <img src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png" alt="Findperty" className="h-8 brightness-0 invert" />
          <Button onClick={() => navigate("/auth")} className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all rounded-full px-5" data-testid="hm-header-cta">
            Empezar gratis
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-24 md:pt-28 pb-12 px-6 lg:px-8 overflow-hidden">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#CCD500]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto">
          <Breadcrumbs items={[{ label: "Haz Match con tu casa ideal" }]} className="mb-8" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#CCD500]">
            <Crown className="w-3.5 h-3.5" /> 30 Master Partners · 10 Regiones · Cobertura Nacional
          </span>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6" data-testid="hm-hero-title">
            Haz Match con tu{" "}
            <span className="bg-gradient-to-r from-[#CCD500] to-[#E5FF00] bg-clip-text text-transparent">casa ideal</span>.
          </h1>
          <p className="mt-7 text-lg text-slate-400 max-w-2xl mx-auto">
            Encuentra al líder inmobiliario de tu ciudad. Explora propiedades curadas por nuestros Master Partners locales en todo México.
          </p>
        </div>
      </section>

      {/* Search + Region filter */}
      <section className="px-6 lg:px-8 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-2xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-4 flex flex-col md:flex-row gap-3 items-stretch">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Busca tu ciudad, estado o agencia..."
                data-testid="hm-search-input"
                className="w-full pl-10 pr-4 h-11 rounded-xl bg-slate-950/50 border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#CCD500]"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              <style>{`.scrollbar-hide::-webkit-scrollbar{display:none;}`}</style>
              {["Todas", ...REGION_GROUPS].map((r) => (
                <button
                  key={r}
                  onClick={() => setActiveRegion(r)}
                  data-testid={`hm-region-${r.toLowerCase()}`}
                  className={`shrink-0 px-4 h-11 rounded-xl text-xs font-semibold transition-all border ${
                    activeRegion === r
                      ? "bg-[#CCD500] text-black border-[#CCD500]"
                      : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="px-6 lg:px-8 pb-24">
        <div className="max-w-7xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-20 rounded-2xl bg-slate-900/40 border border-white/10">
              <Search className="w-10 h-10 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">No encontramos Master Partners con esa búsqueda.</p>
            </div>
          ) : (
            REGION_GROUPS.filter((r) => grouped[r]?.length).map((region) => (
              <div key={region} className="mb-12">
                <div className={`mb-5 p-5 rounded-2xl bg-gradient-to-r ${REGION_META[region]?.color || ""} border-l-4 border-[#CCD500]`}>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#CCD500] font-bold">{region}</p>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-white mt-1">
                    {grouped[region].length} Master Partner{grouped[region].length > 1 ? "s" : ""}
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {grouped[region].map((mp) => (
                    <MPCard key={mp.slug} mp={mp} onClick={() => navigate(`/partner/${mp.slug}`)} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 lg:px-8 pb-20">
        <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-[#CCD500] to-[#9DAB00] p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/20 blur-3xl" />
          <div className="relative">
            <Sparkles className="w-10 h-10 text-black mx-auto mb-4" />
            <h2 className="font-display text-3xl md:text-5xl font-black text-black tracking-tighter mb-4 leading-[0.95]">
              ¿Listo para tu match?
            </h2>
            <p className="text-black/70 text-lg max-w-xl mx-auto mb-8">
              Únete gratis a Findperty. 100% gratis para compradores. Sin tarjeta, sin compromisos.
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/auth")}
              data-testid="hm-final-cta"
              className="bg-black text-[#CCD500] font-bold hover:bg-slate-900 hover:scale-105 rounded-full px-8 h-14 text-base"
            >
              Crear cuenta gratis
              <Building2 className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 lg:px-8 py-10">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500">
          © 2026 Findperty · Cobertura nacional con 30 Master Partners
        </div>
      </footer>
    </div>
  );
};

export default HazMatchPage;
